import { BadgeLabel, Button, Icon } from '@deque/cauldron-react';
import { IssueFieldSource } from '@deque/ui-export-jira';
import { type TFunction } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  type AxeIssueTagFieldCategories,
  AxeTagsOptionLabelByCategory,
  CustomLabelsModal
} from '../CustomLabelsModal';
import { getAxeFieldsOptions, type SelectOption } from '../FieldMapping';
import styles from './Field.css';

type LabelTagButtonProps = {
  value: string;
  label: string;
  onClick: (value: string, fieldSource: IssueFieldSource) => void;
  fieldSource: IssueFieldSource;
};

export type SaveSelectedLabelsArgs = {
  selectedJiraLabels: string[];
  selectedAxeTags: string[];
  selectedAxeField: string;
};

export const getValueToDisplay: Partial<
  Record<IssueFieldSource, (t: TFunction, value: string) => string>
> = {
  [IssueFieldSource.FixedValue]: (_, value) => value,
  [IssueFieldSource.Axe]: (t, value) =>
    getAxeFieldsOptions(t).find(item => item.value === value)?.label || '',
  [IssueFieldSource.AxePartial]: (t, value) =>
    AxeTagsOptionLabelByCategory[value as AxeIssueTagFieldCategories](
      t
    ).replace(/ \(.*\)/, '')
};

const LabelTagButton = ({
  value,
  label,
  onClick,
  fieldSource
}: LabelTagButtonProps) => {
  const { t } = useTranslation();
  const valueToDisplay = getValueToDisplay[fieldSource]?.(t, value) || '';

  return (
    <Button
      variant="badge"
      className={styles.labelTag}
      onClick={() => onClick(value, fieldSource)}
    >
      <BadgeLabel>{label}</BadgeLabel>
      {valueToDisplay}
      <Icon type="close" label={t('Remove {{label}}', { label })} />
    </Button>
  );
};

type LabelsFieldAssignmentProps = {
  index: number;
  axeFields: SelectOption[];
  integrationProductLabels: string[];
};

export const LabelsFieldAssignment = ({
  index,
  axeFields,
  integrationProductLabels
}: LabelsFieldAssignmentProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();

  const [showCustomLabelsModal, setShowCustomLabelsModal] = useState(false);

  const axeField = watch(`fieldMapping.${index}.axeField`);
  const sourceType = watch(`fieldMapping.${index}.sourceType`);

  const removeSelectedLabel = useCallback(
    (label: string, source: IssueFieldSource) => {
      if (!axeField) {
        return;
      }
      const itemToRemove =
        source === IssueFieldSource.AxePartial
          ? `${source}:tags:${label}`
          : `${source}:${label}`;

      const items = axeField
        .split(',')
        .filter((item: string) => item !== itemToRemove)
        .join(',');

      setValue(`fieldMapping.${index}.axeField`, items, {
        shouldValidate: true,
        shouldTouch: true
      });
    },
    [index, axeField, setValue]
  );

  const saveSelectedLabels = useCallback(
    ({
      selectedJiraLabels,
      selectedAxeTags,
      selectedAxeField
    }: SaveSelectedLabelsArgs) => {
      const fixedValues = selectedJiraLabels.map(
        label => `${IssueFieldSource.FixedValue}:${label}`
      );
      const axePartialValues = selectedAxeTags.map(
        tag => `${IssueFieldSource.AxePartial}:tags:${tag}`
      );
      const axeFieldValue =
        selectedAxeField && `${IssueFieldSource.Axe}:${selectedAxeField}`;

      const combinedValue = [
        ...fixedValues,
        ...axePartialValues,
        axeFieldValue
      ].join(',');

      setValue(`fieldMapping.${index}.axeField`, combinedValue);
      setValue(`fieldMapping.${index}.sourceType`, IssueFieldSource.Combined, {
        shouldValidate: true,
        shouldTouch: true
      });
    },
    [index, setValue]
  );

  const selectedModalValues = useMemo(() => {
    if (!axeField) {
      return;
    }

    const result = {
      jiraLabels: [] as string[],
      axeTags: [] as string[],
      axeField: ''
    };

    // For old templates without custom labels values support
    if (sourceType !== IssueFieldSource.Combined) {
      return {
        ...result,
        axeField: axeField
      };
    }

    const items = axeField.split(',');

    items.forEach((item: string) => {
      const [source, value, rest] = item.split(':');

      if (source === IssueFieldSource.FixedValue) {
        result.jiraLabels.push(value);
      } else if (source === IssueFieldSource.AxePartial && value === 'tags') {
        result.axeTags.push(rest);
      } else if (source === IssueFieldSource.Axe) {
        result.axeField = value;
      }
    });

    return result;
  }, [axeField]);

  const selectedLabelsAmount = selectedModalValues
    ? Object.values(selectedModalValues).reduce((amount, item) => {
        amount += Array.isArray(item) ? item.length : item ? 1 : 0;
        return amount;
      }, 0)
    : 0;

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setShowCustomLabelsModal(true)}
        className={styles.editLabelsButton}
      >
        {axeField
          ? t('Edit labels ({{amount}})', { amount: selectedLabelsAmount })
          : t('Select labels')}
      </Button>
      <CustomLabelsModal
        isOpen={showCustomLabelsModal}
        onClose={() => setShowCustomLabelsModal(false)}
        onSave={saveSelectedLabels}
        availableAxeFields={axeFields}
        initialValues={selectedModalValues}
        integrationProductLabels={integrationProductLabels}
      />
      <div className={styles.labelsSelectionsContainer}>
        <span>{t('Selections:')}</span>
        <div className={styles.tagsContainer}>
          {selectedModalValues?.jiraLabels.map(label => (
            <LabelTagButton
              key={label}
              label={t('Jira label:')}
              value={label}
              onClick={removeSelectedLabel}
              fieldSource={IssueFieldSource.FixedValue}
            />
          ))}
          {selectedModalValues?.axeTags.map(tag => (
            <LabelTagButton
              key={tag}
              label={t('Axe tag:')}
              value={tag}
              onClick={removeSelectedLabel}
              fieldSource={IssueFieldSource.AxePartial}
            />
          ))}
          {selectedModalValues?.axeField && (
            <LabelTagButton
              label={t('Axe field:')}
              value={selectedModalValues?.axeField}
              onClick={removeSelectedLabel}
              fieldSource={IssueFieldSource.Axe}
            />
          )}
        </div>
      </div>
    </>
  );
};
